import React from "react";
import { Armchair, Star, Sofa, Heart, Accessibility } from "lucide-react";
import { DecorationLayer } from "./DecorationSvg";

/**
 * SeatMap — Cinema-Look. Zeichnet Sitzreihen in einem dunklen Kino-Auditorium
 * mit gebogener LEINWAND-Fläche oben, leicht bogenförmig angeordneten Reihen,
 * unterschiedlichen Sitztypen (Farbe + Icon) und Gap-Sitzen als Gänge/Treppen.
 *
 * Props:
 *   rows              [{label, seats:[{n, kind, blocked}], angle?, curve?, layer_id?}]
 *   seatTypes         [{key,label,color,sellable,surcharge,override_price,icon, custom_icon_url?}]
 *   seatStatus        map "A1" -> "reserviert"|"bezahlt"|"eingecheckt"|"vorgemerkt"
 *   selected          string[]
 *   onToggle(id)
 *   curve             0-30 (Grad, Standard-Bogen aller Reihen — jede Reihe kann überschreiben)
 *   backgroundUrl     Hintergrundfoto/-grundriss
 *   backgroundOpacity 0..1
 *   layers            [{id, name, color}]
 *   activeLayerId     wenn gesetzt → nur Reihen dieser Ebene rendern
 *   small             Kompaktansicht
 */
const ICONS = { seat: Armchair, star: Star, sofa: Sofa, heart: Heart, wheelchair: Accessibility };

export default function SeatMap({
  rows = [], seatTypes = [], seatStatus = {}, selected = [], onToggle,
  readOnly = false, small = false, curve = 8, decorations = [],
  backgroundUrl = "", backgroundOpacity = 0.35,
  layers = [], activeLayerId = null,
  freeMode = false,
}) {
  const sizeCls = small ? "text-[10px]" : "";
  const typesMap = React.useMemo(() => Object.fromEntries((seatTypes || []).map((t) => [t.key, t])), [seatTypes]);
  const visibleRows = React.useMemo(() => {
    if (!activeLayerId) return rows;
    return (rows || []).filter((r) => (r.layer_id || (layers?.[0]?.id ?? null)) === activeLayerId);
  }, [rows, activeLayerId, layers]);
  // Alle Sitze mit x/y für Free-Mode
  const freeSeats = React.useMemo(() => {
    if (!freeMode) return [];
    const out = [];
    for (const r of visibleRows || []) {
      for (const s of r.seats || []) {
        if (typeof s.x === "number" && typeof s.y === "number") {
          out.push({ row: r, seat: s });
        }
      }
    }
    return out;
  }, [visibleRows, freeMode]);
  return (
    <div className="w-full seatmap-theater rounded-xl p-5 sm:p-8 relative overflow-hidden">
      {backgroundUrl && (
        <div aria-hidden style={{
          position: "absolute", inset: 0, borderRadius: "0.75rem",
          backgroundImage: `url(${backgroundUrl})`,
          backgroundSize: "cover", backgroundPosition: "center",
          opacity: backgroundOpacity, pointerEvents: "none", zIndex: 0,
        }} />
      )}
      <DecorationLayer decorations={decorations} />
      {/* LEINWAND */}
      <div className="mx-auto max-w-3xl mb-8 relative">
        <div className="cinema-screen">
          <div className="cinema-screen__inner">
            <div className="text-center text-[11px] uppercase tracking-[0.4em] text-[#0A0A0A] font-semibold">Leinwand</div>
          </div>
          <div className="cinema-screen__glow" />
        </div>
      </div>

      {freeMode ? (
        <FreeSeatCanvas rows={visibleRows} typesMap={typesMap} seatStatus={seatStatus} selected={selected} onToggle={onToggle} readOnly={readOnly} small={small} />
      ) : (
      /* Sitzreihen (gebogen + rotiert) */
      <div className="flex flex-col gap-2.5 items-center mb-4 relative" style={{ zIndex: 1 }}>
        {visibleRows.map((row, ri) => {
          const rowAngle = Number(row.angle) || 0;
          const rowCurve = (row.curve !== null && row.curve !== undefined && row.curve !== "") ? Number(row.curve) : curve;
          const bend = rowCurve * (ri + 1) / Math.max(visibleRows.length, 1);
          const totalSeats = row.seats.length;
          return (
            <div key={row.label} className="flex items-center gap-2 sm:gap-3"
              style={rowAngle ? { transform: `rotate(${rowAngle}deg)` } : undefined}>
              <div className="w-5 sm:w-6 text-center text-[#A8A398] font-mono text-[10px] sm:text-xs">{row.label}</div>
              <div className="flex gap-1 sm:gap-1.5 items-end" style={{ perspective: "800px" }}>
                {row.seats.map((s, i) => {
                  const rel = totalSeats > 1 ? (i - (totalSeats - 1) / 2) / ((totalSeats - 1) / 2) : 0;
                  const rotY = -rel * bend;
                  const yOff = Math.abs(rel) * bend * 0.35;
                  const id = `${row.label}${s.n}`;
                  const kind = s.kind || "regular";
                  const t = typesMap[kind];
                  const nonSellable = t && t.sellable === false;
                  if (nonSellable) {
                    const isStairs = t?.key === "stairs" || t?.icon === "stairs";
                    const isWall = t?.key === "wall" || t?.icon === "wall";
                    const isPillar = t?.key === "pillar" || t?.icon === "pillar";
                    const cls = isStairs ? "seat-gap seat-gap--stairs"
                      : isWall ? "seat-gap seat-gap--wall"
                      : isPillar ? "seat-gap seat-gap--pillar"
                      : "seat-gap";
                    return <span key={id} className={cls} aria-hidden style={{ transform: `translateY(${yOff}px)` }} title={t?.label} />;
                  }
                  const isSel = selected.includes(id);
                  const status = s.blocked ? "gesperrt" : (seatStatus[id] || "free");
                  const clickable = !readOnly && (status === "free") && !s.blocked;
                  const classToken = isSel ? "selected" : status;
                  const isWheelchair = kind === "wheelchair" || t?.icon === "wheelchair";
                  const isLoveseat = kind === "loveseat" || t?.icon === "heart";
                  const isSofa = kind === "sofa" || t?.icon === "sofa";
                  const border = (!isSel && status === "free" && t?.color && kind !== "regular" && !isWheelchair)
                    ? { borderColor: t.color } : undefined;
                  const IconComp = ICONS[t?.icon] || null;
                  const showBuiltinIcon = IconComp && kind !== "regular" && !t?.custom_icon_url;
                  const extraCls = [
                    isSofa ? "cinema-seat--sofa" : "",
                    isLoveseat ? "cinema-seat--loveseat" : "",
                    isWheelchair && status === "free" ? "cinema-seat--wheelchair" : "",
                  ].filter(Boolean).join(" ");
                  return (
                    <button
                      key={id}
                      data-testid={`seat-${id}`}
                      disabled={!clickable}
                      onClick={() => clickable && onToggle && onToggle(id)}
                      className={`cinema-seat ${classToken} ${sizeCls} ${extraCls}`}
                      style={{ transform: `translateY(${yOff}px) rotateY(${rotY}deg)`, ...border }}
                      title={`${id} — ${t?.label || "Sitz"} · ${status}${t?.surcharge ? ` (+${t.surcharge}€)` : ""}${t?.override_price === 0 ? " · kostenlos" : ""}`}
                    >
                      {t?.custom_icon_url ? (
                        <img src={t.custom_icon_url} alt="" className="cinema-seat__icon" style={{ width: 14, height: 14, objectFit: "contain" }} />
                      ) : showBuiltinIcon ? (
                        <IconComp size={isWheelchair ? (small ? 14 : 18) : (small ? 8 : 10)} className="cinema-seat__icon" strokeWidth={2.2}
                          style={{ color: isWheelchair ? "#FFFFFF" : t.color }} />
                      ) : null}
                      <span className="cinema-seat__num">{s.n}</span>
                    </button>
                  );
                })}
              </div>
              <div className="w-5 sm:w-6 text-center text-[#A8A398] font-mono text-[10px] sm:text-xs">{row.label}</div>
            </div>
          );
        })}
      </div>
      )}

      {/* Ausgang-Markierung */}
      <div className="mt-6 flex justify-between text-[9px] uppercase tracking-[0.32em] text-[#575148] px-2 relative" style={{ zIndex: 1 }}>
        <span className="border-l-2 border-[#7A1921] pl-2 py-1">◀ AUSGANG</span>
        <span className="border-r-2 border-[#7A1921] pr-2 py-1">AUSGANG ▶</span>
      </div>

      <Legend seatTypes={seatTypes} />
    </div>
  );
}

function FreeSeatCanvas({ rows = [], typesMap, seatStatus, selected, onToggle, readOnly, small }) {
  return (
    <div className="relative mx-auto rounded-lg" style={{ height: 480, minHeight: 320, maxWidth: 900, zIndex: 1, border: "1px dashed rgba(212,175,55,0.15)" }}>
      {rows.map((row) => row.seats.map((s) => {
        if (typeof s.x !== "number" || typeof s.y !== "number") return null;
        const id = `${row.label}${s.n}`;
        const kind = s.kind || "regular";
        const t = typesMap[kind];
        const nonSellable = t && t.sellable === false;
        const isSel = selected.includes(id);
        const status = s.blocked ? "gesperrt" : (seatStatus[id] || "free");
        const clickable = !readOnly && !nonSellable && (status === "free") && !s.blocked;
        const classToken = isSel ? "selected" : status;
        const isWheelchair = kind === "wheelchair" || t?.icon === "wheelchair";
        const isSofa = kind === "sofa" || t?.icon === "sofa";
        const isLoveseat = kind === "loveseat" || t?.icon === "heart";
        const extraCls = [
          isSofa ? "cinema-seat--sofa" : "",
          isLoveseat ? "cinema-seat--loveseat" : "",
          isWheelchair && status === "free" ? "cinema-seat--wheelchair" : "",
        ].filter(Boolean).join(" ");
        if (nonSellable) return null; // Gaps/Walls im Free-Mode über Deko-Elemente
        return (
          <button
            key={id} disabled={!clickable}
            onClick={() => clickable && onToggle && onToggle(id)}
            data-testid={`seat-${id}`}
            title={`${id} — ${t?.label || "Sitz"} · ${status}${t?.surcharge ? ` (+${t.surcharge}€)` : ""}`}
            className={`cinema-seat ${classToken} ${extraCls} ${small ? "text-[10px]" : ""} absolute`}
            style={{
              left: `${s.x}%`, top: `${s.y}%`,
              transform: "translate(-50%, -50%)",
              ...(t && !isSel && status === "free" && kind !== "regular" && !isWheelchair && t.color ? { borderColor: t.color } : {}),
            }}
          >
            <span className="cinema-seat__num">{row.label}{s.n}</span>
          </button>
        );
      }))}
    </div>
  );
}

function Legend({ seatTypes = [] }) {
  const base = [
    { c: "free", l: "Frei" },
    { c: "selected", l: "Ausgewählt" },
    { c: "vorgemerkt", l: "Vorgemerkt" },
    { c: "reserviert", l: "Reserviert" },
    { c: "bezahlt", l: "Bezahlt" },
    { c: "gesperrt", l: "Gesperrt" },
  ];
  return (
    <div className="mt-6 pt-4 border-t border-[#1A1A1A]/60 flex flex-wrap gap-3 sm:gap-4 justify-center text-[10px] text-[#A8A398] relative" style={{ zIndex: 1 }}>
      {base.map((i) => (
        <div key={i.c} className="flex items-center gap-1.5">
          <span className={`cinema-seat legend ${i.c}`} style={{ width: 16, height: 16 }} />
          <span className="uppercase tracking-widest">{i.l}</span>
        </div>
      ))}
      {seatTypes.filter((t) => t.key !== "regular" && t.sellable !== false).map((t) => {
        const IconComp = ICONS[t.icon] || null;
        return (
          <div key={t.key} className="flex items-center gap-1.5">
            <span className="cinema-seat legend free relative" style={{ width: 16, height: 16, borderColor: t.color }}>
              {t.custom_icon_url ? <img src={t.custom_icon_url} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
                : (IconComp && <IconComp size={9} className="absolute inset-0 m-auto" style={{ color: t.color }} />)}
            </span>
            <span className="uppercase tracking-widest">{t.label}{t.override_price === 0 ? " · frei" : (t.surcharge ? ` +${t.surcharge}€` : "")}</span>
          </div>
        );
      })}
    </div>
  );
}
